import React, { useState, useEffect, useContext } from 'react';
import RequestService from '../../services/RequestService';
import TableComponent from '../../widgets/TableComponent';

import {
    Typography,
    Breadcrumbs,
    Link,
    Button,
    Box,
    Grid,
    Checkbox,
    FormControlLabel,
    Divider,
    Snackbar,
    Alert,
    CircularProgress,
    TextField,
    Avatar,
} from '@mui/material';
import { UserContext } from '../../contexts/UserContext';
import ZeroDialog from '../../widgets/settings/ZeroDialog';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIconShadow from 'leaflet/dist/images/marker-shadow.png';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import BroadcastDialog from '../../widgets/settings/BroadcastDialog';
import { use } from 'react';

const DefaultIcon = L.icon({
    iconUrl: markerIcon,
    shadowUrl: markerIconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

const SettingsPage = () => {
    const { user, hasPermission } = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [games, setGames] = useState([]);
    const [selectedGames, setSelectedGames] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [useCustomSelection, setUseCustomSelection] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const [photo, setPhoto] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [resetExp, setResetExp] = useState(false);
    const [resetCoin, setResetCoin] = useState(false);

    const [address, setAddress] = useState(null);
    const [addressLoading, setAddressLoading] = useState(false);
    const [addressError, setAddressError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [addressForm, setAddressForm] = useState({
        street: '',
        city: '',
        state: '',
        zipCode: '',
    });
    const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
    const [broadcastTitle, setBroadcastTitle] = useState('');
    const [broadcastMessage, setBroadcastMessage] = useState('');
    const [broadcastSending, setBroadcastSending] = useState(false);

    useEffect(() => {
        // console.log(user);
        const fetchGames = async () => {
            try {
                setLoading(true);
                const data = await RequestService.getGames(user.company.id);
                setGames(data);
            } catch (err) {
                setError('Erro ao carregar jogos.');
            } finally {
                setLoading(false);
            }
        };

        fetchGames();
    }, []);

    useEffect(() => {
        const fetchCompanyPhoto = async () => {
            try {
                setLoading(true);
                const companyData = await RequestService.getCompanyPhoto(
                    user.company.id
                );
                setPhoto(companyData.photoUrl);
            } catch (err) {
                console.error('Erro ao carregar a foto da empresa.', err);
            } finally {
                setLoading(false);
            }
        };

        fetchCompanyPhoto();
    }, [user.company.id]);

    const handleSelectAllChange = (event) => {
        setSelectAll(event.target.checked);
        if (event.target.checked) {
            setSelectedGames(games.map((game) => game.id));
            setUseCustomSelection(false);
        } else {
            setSelectedGames([]);
        }
    };

    const handleUseCustomSelectionChange = (event) => {
        setUseCustomSelection(event.target.checked);
        if (event.target.checked) {
            setSelectAll(false);
        }
    };

    const handleGameSelectionChange = (selectedRows) => {
        setSelectedGames(selectedRows.map((row) => row.id));
    };

    const handleOpenDialog = () => {
        if (selectedGames.length === 0) {
            setSnackbarMessage('Selecione pelo menos um jogo para zerar.');
            setSnackbarOpen(true);
            return;
        }
        setDialogOpen(true);
    };

    const handleIsEditing = () => {
        setIsEditing(!isEditing);
    };

    const handleConfirmReset = async () => {
        let type = '';
        if (resetExp && resetCoin) {
            type = 'both';
        } else if (resetExp) {
            type = 'exp';
        } else if (resetCoin) {
            type = 'coin';
        } else {
            setSnackbarMessage(
                'Selecione ao menos um tipo de ponto para resetar.'
            );
            setSnackbarOpen(true);
            return;
        }

        setDialogOpen(false);
        setLoading(true);

        try {
            await RequestService.resetPoints(user.company.id, type, {
                gameIds: selectedGames,
            });

            setSnackbarMessage('Pontos dos jogos zerados com sucesso!');
            setSnackbarOpen(true);
        } catch (error) {
            setSnackbarMessage('Erro ao zerar pontos dos jogos.');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchAddress = async () => {
            try {
                setAddressLoading(true);
                const data = await RequestService.getCompanyAddress(
                    user.company.id
                );
                if (data) {
                    setAddress(data);
                    setAddressForm({
                        street: data.street,
                        city: data.city,
                        state: data.state,
                        zipCode: data.zipCode,
                    });
                }
            } catch (err) {
                setAddressError('Erro ao carregar endereço.');
            } finally {
                setAddressLoading(false);
            }
        };

        fetchAddress();
    }, []);

    const handleAddressChange = (event) => {
        setAddressForm({
            ...addressForm,
            [event.target.name]: event.target.value,
        });
    };

    const handleUpdateAddress = async () => {
        try {
            setAddressLoading(true);
            const updatedAddress = await RequestService.updateCompanyAddress(
                user.company.id,
                addressForm
            );
            setAddress(updatedAddress);
            setSnackbarMessage('Endereço atualizado com sucesso!');
            setSnackbarOpen(true);
        } catch (error) {
            setSnackbarMessage('Erro ao atualizar o endereço.');
            setSnackbarOpen(true);
        } finally {
            setAddressLoading(false);
        }
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('photo', file);

        try {
            setUploading(true);
            const response = await RequestService.updateCompanyPhoto(
                user.company.id,
                formData
            );
            setPhoto(response.photoUrl);
            setSnackbarMessage('Foto da empresa atualizada com sucesso!');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Erro ao atualizar a foto da empresa', error);
            setSnackbarMessage('Erro ao atualizar a foto da empresa.');
            setSnackbarOpen(true);
        } finally {
            setUploading(false);
        }
    };

    const handleOpenBroadcastDialog = () => {
        setBroadcastTitle('');
        setBroadcastMessage('');
        setBroadcastDialogOpen(true);
    };

    const handleSendBroadcast = async () => {
        if (!broadcastTitle.trim() || !broadcastMessage.trim()) {
            setSnackbarMessage('Preencha título e mensagem antes de enviar.');
            setSnackbarOpen(true);
            return;
        }

        const confirmed = window.confirm(
            'Tem certeza que deseja enviar esta notificação para TODOS os usuários da empresa?'
        );

        if (!confirmed) return;

        try {
            setBroadcastSending(true);
            await RequestService.sendBroadcastNotification({
                title: broadcastTitle.trim(),
                message: broadcastMessage.trim(),
                type: 'SYSTEM', // usando o enum do backend
            });

            setBroadcastDialogOpen(false);
            setSnackbarMessage('Notificação enviada com sucesso!');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Erro ao enviar notificação em broadcast:', error);
            setSnackbarMessage(
                'Erro ao enviar notificação. Tente novamente mais tarde.'
            );
            setSnackbarOpen(true);
        } finally {
            setBroadcastSending(false);
        }
    };

    if (loading) return <div>Carregando...</div>;
    if (error) return <div>{error}</div>;

    return (
        <Box>
            <Grid container justifyContent='start'>
                <Breadcrumbs aria-label='breadcrumb'>
                    <Link underline='hover' color='inherit' href='/'>
                        GameUP
                    </Link>
                    <Link underline='hover' color='inherit'>
                        Início
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Configurações
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: 24,
                        fontWeight: '500',
                        color: '#0C3466',
                        mb: 2,
                    }}
                >
                    Configurações
                </Typography>
            </Grid>

            {hasPermission('SETTINGS_VIEW') && (
                <Box
                    sx={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        padding: '25px',
                    }}
                >
                    <Grid xs={12}>
                        <Typography
                            sx={{
                                fontFamily: 'Poppins',
                                fontSize: '20px',
                                lineHeight: '28px',
                                fontWeight: '500',
                                color: '#0C3466',
                                marginBottom: '8px',
                            }}
                        >
                            Zerar Jogos
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: 'Poppins',
                                fontSize: '14px',
                                fontWeight: '400',
                                color: '#292929',
                                marginBottom: '8px',
                            }}
                        >
                            É recomendável zerar os pontos e ranking dos
                            jogadores ao final de um semestre ou ano.
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: 'Poppins',
                                fontSize: '16px',
                                lineHeight: '24px',
                                fontWeight: '600',
                                color: '#0C3466',
                                marginBottom: '8px',
                            }}
                        >
                            Jogos
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectAll}
                                    onChange={handleSelectAllChange}
                                />
                            }
                            label='Zerar todos os jogos'
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useCustomSelection}
                                    onChange={handleUseCustomSelectionChange}
                                />
                            }
                            label='Escolher manualmente'
                        />
                        {useCustomSelection && (
                            <Box sx={{ mt: 2 }}>
                                <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Selecione os jogos que deseja zerar:
                                </Typography>
                                <TableComponent
                                    columns={[
                                        {
                                            label: 'Nome do Jogo',
                                            field: 'name',
                                        },
                                        {
                                            label: 'Descrição',
                                            field: 'description',
                                        },
                                    ]}
                                    rows={games}
                                    multiSelect
                                    selectedRows={selectedGames}
                                    onSelectionChange={
                                        handleGameSelectionChange
                                    }
                                />
                            </Box>
                        )}
                    </Grid>
                    <Button
                        variant='contained'
                        sx={{
                            backgroundColor: '#E63946',
                            color: '#FFFFFF',
                            mt: 3,
                            mb: 3,
                            ':hover': {
                                backgroundColor: '#FFFFFF',
                                color: '#E63946',
                            },
                        }}
                        onClick={handleOpenDialog}
                    >
                        Zerar Jogos
                    </Button>
                    <Divider />
                    <Box
                        sx={{
                            marginTop: 2,
                            marginBottom: 3,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'Poppins',
                                fontSize: 20,
                                fontWeight: '500',
                                color: '#0C3466',
                                mb: 2,
                            }}
                        >
                            Logomarca da Empresa
                        </Typography>

                        <Grid container spacing={2} alignItems='center'>
                            <Grid item>
                                {loading ? (
                                    <CircularProgress />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 200, // Adjusted size
                                            height: 150,
                                            border: '1px dashed #CCCCCC',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            overflow: 'hidden',
                                            backgroundColor: '#F5F5F5',
                                        }}
                                    >
                                        {photo ? (
                                            <img
                                                src={photo}
                                                alt='Company Logo'
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        ) : (
                                            <AddPhotoAlternateOutlinedIcon
                                                fontSize='large'
                                                color='disabled'
                                            />
                                        )}
                                    </Box>
                                )}
                            </Grid>
                            <Grid item>
                                <Button
                                    variant='contained'
                                    component='label'
                                    sx={{ mt: 1 }}
                                    disabled={uploading}
                                >
                                    {uploading ? 'Enviando...' : 'Alterar Foto'}
                                    <input
                                        type='file'
                                        hidden
                                        onChange={handleFileChange}
                                    />
                                </Button>
                            </Grid>
                        </Grid>

                        <Snackbar
                            open={snackbarOpen}
                            autoHideDuration={3000}
                            onClose={() => setSnackbarOpen(false)}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                        >
                            <Alert
                                onClose={() => setSnackbarOpen(false)}
                                severity='success'
                                variant='filled'
                            >
                                {snackbarMessage}
                            </Alert>
                        </Snackbar>
                    </Box>
                    <Divider />
                    <Typography
                        sx={{
                            fontFamily: 'Poppins',
                            fontSize: '20px',
                            lineHeight: '28px',
                            fontWeight: '500',
                            color: '#0C3466',
                            marginTop: '16px',
                            marginBottom: '8px',
                        }}
                    >
                        Endereço
                    </Typography>

                    <Typography
                        sx={{
                            fontFamily: 'Poppins',
                            fontSize: '16px',
                            lineHeight: '24px',
                            fontWeight: '600',
                            color: '#0C3466',
                            marginBottom: '8px',
                        }}
                    >
                        Endereço da Empresa
                    </Typography>

                    {addressLoading ? (
                        <CircularProgress />
                    ) : address ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography>
                                        <strong>Rua:</strong> {address.street}
                                    </Typography>
                                    <Typography>
                                        <strong>Cidade:</strong> {address.city}
                                    </Typography>
                                    <Typography>
                                        <strong>Estado:</strong> {address.state}
                                    </Typography>
                                    <Typography>
                                        <strong>CEP:</strong> {address.zipCode}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <MapContainer
                                    center={[
                                        address.latitude,
                                        address.longitude,
                                    ]}
                                    zoom={15}
                                    style={{
                                        height: '250px',
                                        width: '100%',
                                        borderRadius: '8px',
                                    }}
                                >
                                    <TileLayer url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
                                    <Marker
                                        position={[
                                            address.latitude,
                                            address.longitude,
                                        ]}
                                    >
                                        <Popup>
                                            {address.street}, {address.city} -{' '}
                                            {address.state}
                                        </Popup>
                                    </Marker>
                                </MapContainer>
                            </Grid>
                        </Grid>
                    ) : (
                        <Typography sx={{ color: 'gray', fontStyle: 'italic' }}>
                            Nenhum endereço cadastrado.
                        </Typography>
                    )}

                    <Button
                        variant='contained'
                        sx={{ marginTop: '8px' }}
                        onClick={handleIsEditing}
                    >
                        Editar endereço
                    </Button>

                    {isEditing === true && (
                        <>
                            <Typography
                                sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}
                            >
                                Atualizar Endereço
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        label='Rua'
                                        name='street'
                                        value={addressForm.street}
                                        onChange={handleAddressChange}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label='Cidade'
                                        name='city'
                                        value={addressForm.city}
                                        onChange={handleAddressChange}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField
                                        label='Estado'
                                        name='state'
                                        value={addressForm.state}
                                        onChange={handleAddressChange}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField
                                        label='CEP'
                                        name='zipCode'
                                        value={addressForm.zipCode}
                                        onChange={handleAddressChange}
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>

                            <Button
                                variant='contained'
                                sx={{ marginTop: '8px' }}
                                onClick={handleUpdateAddress}
                            >
                                {addressLoading
                                    ? 'Salvando...'
                                    : 'Salvar Endereço'}
                            </Button>
                        </>
                    )}

                    {user.company.id === 21 && (
                        <>
                            <Divider sx={{ my: 3 }} />

                            <Box sx={{ mb: 3 }}>
                                <Typography
                                    sx={{
                                        fontFamily: 'Poppins',
                                        fontSize: '20px',
                                        fontWeight: '500',
                                        color: '#0C3466',
                                        mb: 1,
                                    }}
                                >
                                    Enviar mensagem para todos os jogadores
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: 'Poppins',
                                        fontSize: '14px',
                                        fontWeight: '400',
                                        color: '#292929',
                                        mb: 2,
                                    }}
                                >
                                    Use esta opção para enviar uma notificação
                                    push para todos os jogadores. Ideal para
                                    avisos importantes, comunicados e lembretes.
                                </Typography>

                                <Button
                                    variant='contained'
                                    sx={{ marginTop: '8px' }}
                                    onClick={handleOpenBroadcastDialog}
                                >
                                    Enviar notificação
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            )}
            {!hasPermission('SETTINGS_VIEW') && (
                <Box
                    sx={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        padding: '25px',
                    }}
                >
                    <Typography variant='h6'>
                        Você não tem permissão para acessar esse recurso.
                    </Typography>
                    <Typography variant='body2'>
                        Entre em contato com o administrador.
                    </Typography>
                </Box>
            )}

            <ZeroDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onConfirm={handleConfirmReset}
                selectedGames={games.filter((game) =>
                    selectedGames.includes(game.id)
                )}
                resetExp={resetExp}
                setResetExp={setResetExp}
                resetCoin={resetCoin}
                setResetCoin={setResetCoin}
            />

            <BroadcastDialog
                open={broadcastDialogOpen}
                onClose={() => setBroadcastDialogOpen(false)}
                title={broadcastTitle}
                setTitle={setBroadcastTitle}
                message={broadcastMessage}
                setMessage={setBroadcastMessage}
                onSend={handleSendBroadcast}
                sending={broadcastSending}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity='success'
                    variant='filled'
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SettingsPage;
